import { canonicalPlace } from "./layout.ts";
import { sameJson } from "./same-json.ts";
import type { MapDocument, WorldDocument } from "./types.ts";

/**
 * The world's own map commands (ADR-021's amendment "several maps"): a whole map added at its place in the
 * list or taken out of it, and a map renamed. Each is pure, returns a new document and throws on a precondition
 * the recorded command does not meet, which is what makes their undo exact.
 */

/**
 * A map in its saved form: a place's depth of 0 dropped (ADR-122's note "how P1.4-T135 is built", item 5), so a
 * map added here holds what a reader of the saved text and a live replica hold. The map itself when it is
 * already so.
 *
 * @param map The map.
 * @returns The map, its place without a depth of 0.
 */
export function canonicalMap(map: MapDocument): MapDocument {
  if (map.place?.depth !== 0) {
    return map;
  }
  return { ...map, place: canonicalPlace(map.place) as NonNullable<MapDocument["place"]> };
}

/**
 * Inserts a whole map at its place in the world's list, its place in its saved form.
 *
 * @param world The document; never mutated.
 * @param index Where the map goes, 0 to the list's length.
 * @param map The map.
 * @returns The new document.
 * @throws Error when the index is outside the list or the world already holds a map of the id.
 */
export function addMapTo(world: WorldDocument, index: number, map: MapDocument): WorldDocument {
  if (!Number.isInteger(index) || index < 0 || index > world.maps.length) {
    throw new Error(`map insert index ${index} is outside [0, ${world.maps.length}]`);
  }
  if (world.maps.some((m) => m.id === map.id)) {
    throw new Error(`map ${map.id} already exists`);
  }
  return { ...world, maps: [...world.maps.slice(0, index), canonicalMap(map), ...world.maps.slice(index)] };
}

/**
 * Takes the recorded map out of the world's list.
 *
 * @param world The document; never mutated.
 * @param index Where the map is.
 * @param map The map as recorded, compared whole with the one at the index.
 * @returns The new document.
 * @throws Error when another map is at the index, the map there differs from the recorded one, it is the
 *   world's last map, or a seam still names it (ADR-109 decision 6: a map leaves with its seams, in one
 *   compound command).
 */
export function removeMapFrom(world: WorldDocument, index: number, map: MapDocument): WorldDocument {
  const existing = world.maps[index];
  if (existing === undefined || existing.id !== map.id) {
    throw new Error(`map ${map.id} is not at index ${index}`);
  }
  if (!sameJson(existing, canonicalMap(map))) {
    throw new Error(`map ${map.id} does not match the recorded map`);
  }
  if (world.maps.length === 1) {
    throw new Error(`map ${map.id} is the world's last; a world keeps at least one map`);
  }
  const seam = (world.seams ?? []).find((s) => s.a === map.id || s.b === map.id);
  if (seam !== undefined) {
    throw new Error(`map ${map.id} is named by seam ${seam.a}/${seam.b}; remove the seam first`);
  }
  return { ...world, maps: [...world.maps.slice(0, index), ...world.maps.slice(index + 1)] };
}

/**
 * Renames a map, its id and everything else kept.
 *
 * @param world The document; never mutated.
 * @param mapId The map.
 * @param before The name it must hold now.
 * @param after The name it takes.
 * @returns The new document.
 * @throws Error when the world has no such map or its name is not `before`.
 */
export function renameMapIn(world: WorldDocument, mapId: string, before: string, after: string): WorldDocument {
  const map = world.maps.find((m) => m.id === mapId);
  if (map === undefined) {
    throw new Error(`map ${mapId} does not exist`);
  }
  if (map.name !== before) {
    throw new Error(`map ${mapId} name does not match the recorded before name`);
  }
  return { ...world, maps: world.maps.map((m) => (m.id === mapId ? { ...m, name: after } : m)) };
}
